<script>
import { Splide } from '@splidejs/splide';
import CalendarPosts from './CalendarPosts.vue';
import CalendarDate from './CalendarDate.vue';
import CalendarDateDay from './CalendarDateDay.vue';
import CalendarDateMonth from './CalendarDateMonth.vue';
import CalendarDateYear from './CalendarDateYear.vue';
import CalendarSliderLayout from './CalendarSliderLayout.vue';
import {
    getFormattedDate,
    compareFormattedDates
} from '../utils/date-helper.js';
import {
    centerSliderByItem,
    centerSliderByPage,
    updateSlider
} from '../utils/date-slider-helper.js';

export default {
    name: 'CalendarContainer',
    components: {
        CalendarPosts,
        CalendarDate,
        CalendarDateDay,
        CalendarDateMonth,
        CalendarDateYear,
        CalendarSliderLayout
    },
    mounted() {
        this.initSliders();
    },
    props: {
        date: {
            type: Date,
            required: true
        },
        posts: {
            type: Array,
            default: () => []
        },
        dateConfig: {
            type: Object,
            default: () => ({ years: [], months: [], days: [] })
        }
    },
    data() {
        return {
            yearSlider: null,
            monthsSlider: null,
            daysSlider: null
        };
    },
    methods: {
        initSliders() {
            this.yearSlider = new Splide('#years', {
                perPage: 7,
                fixedWidth: 184,
                padding: '0 16px',
                arrows: false,
                pagination: false,
                start: this.activeYearIndex
            }).mount();
            this.monthsSlider = new Splide('#months', {
                perPage: 7,
                pagination: false,
                start: this.activeMonthIndex
            }).mount();
            this.daysSlider = new Splide('#days', {
                perPage: 7,
                gap: 24,
                start: this.activeSliderPage,
                pagination: false,
                perMove: 7
            }).mount();
            centerSliderByItem(this.yearSlider, this.activeYearIndex);
            centerSliderByItem(this.monthsSlider, this.activeMonthIndex);
            centerSliderByPage(this.daysSlider, this.activeSliderPage);
            this.sliderMoveListener();
        },
        sliderMoveListener() {
            this.daysSlider.on('moved', (slideIndex) => {
                this.$emit('update-posts', this.dateConfig.days[slideIndex]);
            });
        },
        getColorDots(day) {
            const postsByDay = this.posts.filter(
                (item) =>
                    new Date(item.date).setHours(0, 0, 0, 0) ===
                    day.setHours(0, 0, 0, 0)
            );

            return postsByDay
                .filter((item) => !!item.color)
                .map((item) => item.color);
        }
    },
    computed: {
        formattedDate() {
            return getFormattedDate(this.date);
        },
        activeYearIndex() {
            return this.dateConfig.years.findIndex(
                (item) => this.date.getYear() === item
            );
        },
        activeMonthIndex() {
            return this.date.getMonth();
        },
        activeDayIndex() {
            return this.dateConfig.days.findIndex((day) =>
                compareFormattedDates(this.formattedDate, getFormattedDate(day))
            );
        },
        activeSliderPage() {
            return Math.floor(this.activeDayIndex / 7);
        }
    },
    watch: {
        date() {
            updateSlider(this, this.monthsSlider);
            updateSlider(this, this.daysSlider);
            this.$nextTick(() => {
                centerSliderByPage(this.daysSlider, this.activeSliderPage);
                centerSliderByItem(this.monthsSlider, this.activeMonthIndex);
            });
        }
    }
};
</script>

<template>
    <div class="sw-calendar shadow-base rounded-block bg-white pb-8">
        <CalendarDate
            :date="date"
            :date-config="dateConfig"
            @update-date="$emit('update-date', $event)"
        >
            <template #years="{ years, setYear }">
                <CalendarSliderLayout
                    id="years"
                    class="sw-calendar-slider-years"
                >
                    <li
                        v-for="(year, index) in years"
                        :key="index"
                        class="splide__slide flex justify-center first:pl-8 last:mr-10 last:pr-16"
                        @click="setYear(year)"
                    >
                        <CalendarDateYear
                            :year="year"
                            :formatted-date="formattedDate"
                        />
                    </li>
                </CalendarSliderLayout>
            </template>
            <template #months="{ months, setMonth }">
                <CalendarSliderLayout
                    id="months"
                    class="sw-calendar-slider-months mb-8 px-[90px]"
                >
                    <li
                        v-for="(month, index) in months"
                        :key="index"
                        class="splide__slide last:mr-0"
                        @click="setMonth(index)"
                    >
                        <CalendarDateMonth
                            :formatted-date="formattedDate"
                            :month="month"
                            :index="index + 1"
                        />
                    </li>
                </CalendarSliderLayout>
            </template>
            <template #days="{ days, setDate }">
                <CalendarSliderLayout
                    id="days"
                    class="sw-calendar-slider-days px-[90px]"
                >
                    <li
                        v-for="(day, index) in days"
                        :key="index"
                        class="splide__slide"
                        @click="setDate(day)"
                    >
                        <CalendarDateDay
                            :formatted-date="formattedDate"
                            :day="day"
                            :color-dots="getColorDots(day)"
                        />
                    </li>
                </CalendarSliderLayout>
            </template>
        </CalendarDate>
        <CalendarPosts
            :posts="posts"
            :date="date"
            @set-previous-day="$emit('update-date', $event)"
            @set-today="$emit('update-date', $event)"
        />
    </div>
</template>

<style lang="postcss">
.sw-calendar-slider-years {
    @apply relative mr-20 -ml-3;

    &::before,
    &::after {
        content: '';
        @apply w-22 absolute top-0 z-30 block h-full;
    }

    &::before {
        @apply from-blue-lightest left-3 w-28 bg-gradient-to-r to-transparent;
    }

    &::after {
        @apply to-blue-lightest right-0 bg-gradient-to-r from-transparent;
    }
}
.sw-calendar-slider-months {
    .sw-slider-arrows {
        top: 6px;
    }

    &::before,
    &::after {
        content: '';
        @apply w-22 absolute top-0 z-20 block h-full;
    }

    &::before {
        @apply left-[90px] z-10 w-28 bg-gradient-to-r from-white to-transparent;
    }

    &::after {
        @apply right-[90px] bg-gradient-to-r from-transparent to-white;
    }
}

.sw-calendar-slider-days {
    .sw-slider-arrows {
        top: 46px;
    }
}
</style>
